import type { BizyPageType } from '../store/use-bizy-page-context-store';

const promptKeysByPage: Partial<Record<BizyPageType, string[]>> = {
	home: [
		'suggestedPrompts.home.whatsGoingOn',
		'suggestedPrompts.home.whoToRecognize',
		'suggestedPrompts.home.upcomingCelebration',
		'suggestedPrompts.home.teamValues',
		'suggestedPrompts.home.notRecognized',
		'suggestedPrompts.home.helpRecognize',
	],

	'1on1_list': [
		'suggestedPrompts.1on1_list.needsAgenda',
		'suggestedPrompts.1on1_list.notMetRecently',
		'suggestedPrompts.1on1_list.focusThisWeek',
		'suggestedPrompts.1on1_list.openActionItems',
		'suggestedPrompts.1on1_list.prioritize',
		'suggestedPrompts.1on1_list.catchUp',
	],

	'1on1': [
		'suggestedPrompts.1on1.talkingPoints',
		'suggestedPrompts.1on1.actionItems',
		'suggestedPrompts.1on1.recognizedFor',
		'suggestedPrompts.1on1.lastMeeting',
		'suggestedPrompts.1on1.checkinTrend',
		'suggestedPrompts.1on1.onPlate',
	],

	goals: [
		'suggestedPrompts.goals.overdue',
		'suggestedPrompts.goals.comingDue',
		'suggestedPrompts.goals.writeGoal',
		'suggestedPrompts.goals.structureGoal',
		'suggestedPrompts.goals.focusing',
	],

	recaps: ['suggestedPrompts.recaps.whatAreRecaps', 'suggestedPrompts.recaps.howToUse', 'suggestedPrompts.recaps.howToSetUp'],

	rewards: [
		'suggestedPrompts.rewards.howRewardsWork',
		'suggestedPrompts.rewards.typesAvailable',
		'suggestedPrompts.rewards.howToRedeem',
		'suggestedPrompts.rewards.redemptionHistory',
	],

	team: [
		'suggestedPrompts.team.coachingSupport',
		'suggestedPrompts.team.checkinsTrending',
		'suggestedPrompts.team.notRecognized',
		'suggestedPrompts.team.discussThisWeek',
		'suggestedPrompts.team.aboveAndBeyond',
		'suggestedPrompts.team.teamValues',
		'suggestedPrompts.team.upcomingMilestones',
		'suggestedPrompts.team.teamSummary',
	],

	incentives: [
		'suggestedPrompts.incentives.howIncentivesWork',
		'suggestedPrompts.incentives.howToParticipate',
		'suggestedPrompts.incentives.howToKnowCompleted',
	],

	advice: [
		'suggestedPrompts.advice.summarize',
		'suggestedPrompts.advice.patterns',
		'suggestedPrompts.advice.writeAdvice',
		'suggestedPrompts.advice.improve',
		'suggestedPrompts.advice.whatToGive',
		'suggestedPrompts.advice.respond',
	],

	profile_self: [
		'suggestedPrompts.profile_self.recognizedFor',
		'suggestedPrompts.profile_self.recentlyRecognized',
		'suggestedPrompts.profile_self.recognizesMeOften',
		'suggestedPrompts.profile_self.activityOverTime',
		'suggestedPrompts.profile_self.highlights',
		'suggestedPrompts.profile_self.knownFor',
	],

	profile_other: [
		'suggestedPrompts.profile_other.recentRecognition',
		'suggestedPrompts.profile_other.writeRecognition',
		'suggestedPrompts.profile_other.values',
		'suggestedPrompts.profile_other.lastRecognized',
		'suggestedPrompts.profile_other.currentGoals',
		'suggestedPrompts.profile_other.prepareOneOnOne',
		'suggestedPrompts.profile_other.relationship',
	],

	profile_bot: [],

	bookmarks: ['suggestedPrompts.bookmarks.whatAreBookmarks', 'suggestedPrompts.bookmarks.howToUse', 'suggestedPrompts.bookmarks.addNotes'],

	settings: [
		'suggestedPrompts.settings.connectSlackTeams',
		'suggestedPrompts.settings.notificationSettings',
		'suggestedPrompts.settings.updateProfile',
		'suggestedPrompts.settings.rewardPreferences',
		'suggestedPrompts.settings.integrations',
	],

	admin: ['suggestedPrompts.admin.participationTrends', 'suggestedPrompts.admin.lowestEngagement', 'suggestedPrompts.admin.improveRates'],
};

function shuffleArray<T>(arr: T[]): T[] {
	const shuffled = [...arr];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}

export function getRandomPromptKeys(pageType: BizyPageType, count = 3): string[] {
	const pool = promptKeysByPage[pageType] ?? promptKeysByPage.home ?? [];
	return shuffleArray(pool).slice(0, count);
}

const trialPromptKeysByPage: Partial<Record<BizyPageType, string[]>> = {
	home: ['trialPrompts.home.getStarted', 'trialPrompts.home.firstRecognition', 'trialPrompts.home.inviteTeam'],

	team: ['trialPrompts.team.setUpTeam', 'trialPrompts.team.invitePeople', 'trialPrompts.team.whatCanIDo'],

	'1on1_list': ['trialPrompts.1on1.howMeetingsWork', 'trialPrompts.1on1.setUpFirst', 'trialPrompts.1on1.bizyInMeetings'],

	'1on1': ['trialPrompts.1on1.howMeetingsWork', 'trialPrompts.1on1.setUpFirst', 'trialPrompts.1on1.bizyInMeetings'],

	goals: ['trialPrompts.goals.howGoalsWork', 'trialPrompts.goals.writeFirst', 'trialPrompts.goals.shareGoal'],

	recaps: ['trialPrompts.recaps.whatAreRecaps', 'trialPrompts.recaps.howToSetUp', 'trialPrompts.recaps.performanceReviews'],

	advice: ['trialPrompts.advice.howAdviceWorks', 'trialPrompts.advice.askForAdvice', 'trialPrompts.advice.adviceVsRecognition'],

	profile_self: ['trialPrompts.profile.howProfileWorks', 'trialPrompts.profile.whatShowsUp', 'trialPrompts.profile.pointsAndValues'],

	profile_other: ['trialPrompts.profile.howProfileWorks', 'trialPrompts.profile.whatShowsUp', 'trialPrompts.profile.pointsAndValues'],

	profile_bot: [],
};

export function getTrialPromptKeys(pageType: BizyPageType, count = 3): string[] | null {
	if (!(pageType in trialPromptKeysByPage)) return null;
	const pool = trialPromptKeysByPage[pageType] ?? [];
	return shuffleArray(pool).slice(0, count);
}
